import { useState, useCallback, useEffect, useMemo, lazy, Suspense, useSyncExternalStore } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getDisplayName, getSenderFromMetadata } from "@/lib/utils/display-name";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Globe, Phone, MapPin, Users, DollarSign, Calendar, ExternalLink, Mail, Star, Video, FileText, Paperclip, Sparkles, StickyNote, Loader2, ArrowDownLeft, ArrowUpRight, ArrowLeft, Briefcase, MoreHorizontal, ListPlus, Trash2, CheckSquare, ChevronDown, ChevronRight, List as ListIcon, Activity, Clock, UserCircle2, Link as LinkIcon, Building2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import AddToListDialog from "@/components/shared/AddToListDialog";
import ConfirmDeleteDialog from "@/components/shared/ConfirmDeleteDialog";
import { MeetingsTable, EmailsTable, FilesTable, NotesTable, PeopleTable, TasksTable } from "@/components/shared/DrillDownTable";
import RecordCallsTab from "@/components/shared/RecordCallsTab";
import type { NoteItem } from "@/components/shared/SheetDrillDown";
import CompanyLogo from "@/components/shared/CompanyLogo";
import EntityListBadges from "@/components/shared/EntityListBadges";
import MoreFieldsPanel, { MoreFieldRow, type MorePanelField } from "@/components/shared/MoreFieldsPanel";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import { useActiveGridColumnPrefs } from "@/hooks/useActiveGridColumnPrefs";
import { useDebouncedCompanyUpdate } from "@/lib/hooks/use-debounced-company-update";
import { useCompanyCustomFields } from "@/lib/hooks/use-company-custom-fields";
import UserAvatar from "@/components/shared/Avatar";
import LinkedInIcon from "@/components/shared/LinkedInIcon";
import EditableDetailField from "@/components/shared/EditableDetailField";
import EditableCell from "@/components/shared/EditableCell";
import EditableLocationField from "@/components/shared/EditableLocationField";
import EditableIndustryField from "@/components/shared/EditableIndustryField";
import MultiValueDetailField from "@/components/shared/MultiValueDetailField";
import { formatPhone } from "@/lib/format-phone";
import { activeLanguage, formatNumber } from "@/lib/i18n/format";
import { useFormatters } from "@/lib/i18n/useFormatters";
import { accountEditStore, type AccountOverride } from "@/lib/utils/account-edit-store";
import { validateLinkedinUrl } from "@/lib/utils/linkedin";
import { EmailDrillDown, NoteDrillDown } from "@/components/shared/SheetDrillDown";
// Lazy-loaded to break the CompanyDetailSheet <-> DealDetailSheet circular import
// (DealDetailSheet statically imports CompanyDetailSheet). See QUICK_REFERENCE bug #5.
const DealDetailSheet = lazy(() => import("@/components/shared/DealDetailSheet"));
import AssociatedCompanies from "@/components/shared/AssociatedCompanies";
import { companyAssociationsStore, mergeAssociatedCompanies, type AssociatedCompanyEntry } from "@/lib/utils/company-associations-store";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import PersonDetailSheet from "@/components/shared/PersonDetailSheet";
import TaskDetailSheet from "@/components/shared/TaskDetailSheet";
import MeetingIntelligenceSheet from "@/components/shared/MeetingIntelligenceSheet";
import MeetingBriefingSheet from "@/components/shared/MeetingBriefingSheet";
import LinkedText from "@/components/shared/LinkedText";
import type { NavigationItem, DealItem } from "@/components/shared/SheetDrillDown";
import { cn } from "@/lib/utils";
import { people, companies as allCompanies, deals as allDeals } from "@/data/dummy";
import { useFavorites } from "@/contexts/FavoritesContext";
import { useGetAccountLazyQuery, DdbEngagementType, NoteStatusEnum } from "@/lib/graphql/types-and-hooks";
import { useTaskMutations } from "@/lib/hooks/use-task-mutations";
import { useDeleteNote } from "@/lib/hooks/use-delete-note";
import { isLovablePreview } from "@/lib/utils/environment";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import { useIsReadOnly } from "@/lib/rbac";
import { toast } from "sonner";

import { useAccountUpdate } from "@/lib/hooks/use-account-update";
import { useAccountEngagements } from "@/lib/hooks/use-account-engagements";
import { useAccountFiles } from "@/lib/hooks/use-account-files";
import { useUploadFile } from "@/lib/hooks/use-upload-file";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useAccountNotes } from "@/lib/hooks/use-account-notes";
import { useTasks as useDummyTasks } from "@/contexts/TasksContext";
import { useTasks as useBackendTasks } from "@/lib/hooks/use-tasks";
import { useCompanyMutations } from "@/lib/hooks/use-company-mutations";
import { resolveSheetDeleteAction } from "@/lib/utils/sheet-delete";
import { EmployeeRanges, RevenueRanges, findMatchingRange } from "@/lib/types/ranges";
import NoteEditor from "@/components/shared/NoteEditor";
import { useEntityByName } from "@/lib/hooks/use-entity-by-name";
import type { PartialBlock } from "@blocknote/core";

const locationSuggestions = [...new Set([...people.map(p => p.location).filter(Boolean), ...allCompanies.map(c => c.location).filter(Boolean)])] as string[];

type SummarySection = {
  title: string;
  bullets?: (string | { text: string })[];
  body?: string;
};

function summaryToBlocks(summary: SummarySection[]): PartialBlock[] {
  const blocks: PartialBlock[] = [];

  summary.forEach((section) => {
    if (!section || (!section.bullets && !section.body)) return;

    blocks.push({
      type: "heading",
      props: { level: 3 },
      content: [{ type: "text" as const, text: section.title, styles: {} }],
    });

    if (Array.isArray(section.bullets)) {
      section.bullets.forEach((bullet) => {
        const text = typeof bullet === 'string' ? bullet : bullet.text;
        blocks.push({
          type: "bulletListItem",
          content: [{ type: "text" as const, text, styles: {} }],
        });
      });
    }

    if (section.body && !section.bullets) {
      blocks.push({
        type: "paragraph",
        content: [{ type: "text" as const, text: section.body, styles: {} }],
      });
    }
  });

  return blocks;
}

// Helper function to format range values (matches coffee-ui NumberUtils.getRangeValue)
function formatRangeValue(min?: number | null, max?: number | null, prefix: string = ''): string {
  const prettyNumber = (n: number) => {
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(2).replace(/\.?0+$/, '')}K`;
    return formatNumber(n, activeLanguage());
  };

  const numMin = Number(min);
  const numMax = Number(max);

  if (min && !max) {
    if (numMin > 100_000) {
      return `${prefix}${prettyNumber(numMin)} and Over`;
    }
    return `${prefix}${prettyNumber(numMin)}+`;
  }

  if (min && max) {
    if (numMax < numMin || Math.abs(numMax - numMin) < 1e-10) {
      if (numMin > 100_000) {
        return `${prefix}${prettyNumber(numMin)} and Over`;
      }
      return `${prefix}${prettyNumber(numMin)}+`;
    }
    return `${prefix}${prettyNumber(numMin)} to ${prefix}${prettyNumber(numMax)}`;
  }

  if (!min && max) {
    return `Under ${prefix}${prettyNumber(numMax)}`;
  }

  return '-';
}

interface Company {
  id: string;
  name: string;
  website?: string;
  domain?: string;
  location?: string;
  linkedin?: string;
  phone?: string;
  industry?: string;
  employees?: string;
  revenue?: string;
  description?: string;
  lastEmail?: string;
  lastMeeting?: string;
  nextMeeting?: string;
}

interface CompanyDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  hideOverlay?: boolean; // Hide overlay when opened as a nested/stacked sheet
  onBack?: () => void; // Show back arrow to return to previous sheet
  onFieldUpdate?: (field: string, value: string) => void; // Notify parent of field changes
  onDelete?: (id: string) => void; // Notify parent when record is deleted
}

// Single-value fields that also live in AccountOverride — edits to these are
// mirrored into accountEditStore for cross-page overlays. (websites/phones
// multi-value paths merge separately where they're edited.)
const ACCOUNT_STORE_FIELDS = new Set<keyof AccountOverride>(["name", "website", "phone", "linkedin", "location", "industry"]);

const dummyMeetings = [
  { id: "1", title: "Quarterly Business Review", date: "Feb 27, 2026", duration: "45 min", attendees: ["Sarah Chen", "Doug"] },
  { id: "2", title: "Product Roadmap Sync", date: "Feb 18, 2026", duration: "30 min", attendees: ["Sarah Chen", "Alex Kim", "Doug"] },
  { id: "3", title: "Onboarding Kickoff", date: "Feb 5, 2026", duration: "60 min", attendees: ["Alex Kim", "Doug", "Lisa Park"] },
  { id: "4", title: "Contract Negotiation", date: "Jan 22, 2026", duration: "45 min", attendees: ["Sarah Chen", "Doug"] },
  { id: "5", title: "Initial Discovery", date: "Jan 10, 2026", duration: "30 min", attendees: ["Sarah Chen", "Doug", "Mike Chen"] },
];

const dummyFiles = [
  { id: "1", name: "Q4_Partnership_Agreement.pdf", size: "2.4 MB", date: "Feb 25, 2026", from: "Sarah Chen", type: "pdf" },
  { id: "2", name: "Product_Roadmap_2026.pptx", size: "8.1 MB", date: "Feb 18, 2026", from: "Alex Kim", type: "pptx" },
  { id: "3", name: "Pricing_Sheet_v3.xlsx", size: "340 KB", date: "Feb 10, 2026", from: "Doug", type: "xlsx" },
  { id: "4", name: "NDA_Signed.pdf", size: "1.1 MB", date: "Jan 22, 2026", from: "Sarah Chen", type: "pdf" },
  { id: "5", name: "Integration_Specs.docx", size: "520 KB", date: "Jan 15, 2026", from: "Alex Kim", type: "docx" },
];

const dummyEmails = [
  { id: "1", subject: "Re: Platform Migration Timeline", date: "Feb 28, 2026", from: "Sarah Chen", to: ["Doug"], cc: ["Lisa Park"], direction: "inbound" as const },
  { id: "2", subject: "Proposal Draft v2", date: "Feb 25, 2026", from: "Doug", to: ["Sarah Chen"], direction: "outbound" as const },
  { id: "3", subject: "Re: Pricing Questions", date: "Feb 20, 2026", from: "Alex Kim", to: ["Doug", "Lisa Park"], direction: "inbound" as const },
  { id: "4", subject: "Follow-up: Discovery Call", date: "Feb 15, 2026", from: "Doug", to: ["Sarah Chen", "Alex Kim"], direction: "outbound" as const },
];

/**
 * The Summary tab's ID — a value the Tabs components compare against, never
 * copy (its label comes from `detailSheets.tabSummary`). Named so the i18n lint
 * does not read it as a hardcoded string where it appears inside JSX.
 */
const SUMMARY_TAB_ID = "summary";

export default function CompanyDetailSheet({ open, onOpenChange, company, hideOverlay = false, onBack, onFieldUpdate, onDelete }: CompanyDetailSheetProps) {
  const { t } = useTranslation();
  const { formatDate, formatDateTime, formatTime } = useFormatters();
  const [activeTab, setActiveTab] = useState(SUMMARY_TAB_ID);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { deleteAccount } = useCompanyMutations();
  // Resolves attendee / sender display names to real records — backend search
  // in production, dummy people in preview.
  const { resolvePerson } = useEntityByName();
  const { scheduleUpdate: scheduleCompanyFieldUpdate } = useDebouncedCompanyUpdate(500);
  const isReadOnly = useIsReadOnly();
  // Detail-sheet kebab actions are all mutations — hide for read-only.
  const drillDownActionsEnabled = useFeatureFlag(FEATURE_FLAGS.DRILL_DOWN_ACTIONS) && !isReadOnly;
  // HubSpot-synced calls (coffee#4959); hides the tab and skips its query when off.
  const callsTabEnabled = useFeatureFlag(FEATURE_FLAGS.CALLS_TAB);
  const [navStack, setNavStack] = useState<NavigationItem[]>([]);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Nested PersonDetailSheet state
  const [personSheetOpen, setPersonSheetOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<{ id: string; name: string; title?: string; company?: string; email?: string; phone?: string; } | null>(null);

  // TaskDetailSheet state (stacked sheet) — lets the task title open the task
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Nested CompanyDetailSheet state (for clicking on related companies)
  const [nestedCompanySheetOpen, setNestedCompanySheetOpen] = useState(false);
  const [nestedSelectedCompany, setNestedSelectedCompany] = useState<{ id: string; name: string; industry?: string; description?: string; location?: string; website?: string; employees?: string; revenue?: string; } | null>(null);

  // Nested DealDetailSheet state (for clicking on this company's deals)
  const [nestedDealSheetOpen, setNestedDealSheetOpen] = useState(false);
  const [nestedSelectedDeal, setNestedSelectedDeal] = useState<DealItem | null>(null);

  // Meeting/Briefing intelligence sheet state
  const [meetingIntelligenceOpen, setMeetingIntelligenceOpen] = useState(false);
  const [meetingIntelligenceNote, setMeetingIntelligenceNote] = useState<{ id: string; name: string; company: string; people: string[]; date: string } | null>(null);
  const [meetingIntelligenceTab, setMeetingIntelligenceTab] = useState("summary");
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [briefingMeeting, setBriefingMeeting] = useState<{ id: string; title: string; date: string; time: string; duration: string; type: string; attendees: string[]; link?: string } | null>(null);

  // Editable overrides
  const [overrides, setOverrides] = useState<Partial<Company>>({});
  const [websites, setWebsites] = useState<string[]>([]);
  const [websitesEdited, setWebsitesEdited] = useState(false); // Track if user has manually edited websites
  // Multi-value phones (feature-flagged; UI-only until backend supports additional phones)
  const [phones, setPhones] = useState<string[]>([]);
  const [associatedCompanies, setAssociatedCompanies] = useState<AssociatedCompanyEntry[]>([]);

  // Subscribe to the shared account edit store so edits made in the grid (or
  // other surfaces) flow into the drill-down view without a reload.
  const storeOverride = useSyncExternalStore(
    accountEditStore.subscribe,
    () => (company?.id ? accountEditStore.get(company.id) : undefined),
    () => undefined,
  );

  // Reset websitesEdited flag when sheet closes to allow fresh sync on next open
  useEffect(() => {
    if (!open) {
      setWebsitesEdited(false);
    }
  }, [open]);

  // Backend data hooks
  const workspaceId = useWorkspaceId();
  const [getAccount, { data: accountData, loading: accountLoading }] = useGetAccountLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // Backend account update hook
  const {
    updateName,
    updateDomain,
    updateAdditionalDomains,
    updatePhone,
    updateLocation,
    updateLinkedIn,
    updateIndustry,
    updateEmployees,
    updateRevenue,
    updateDescription,
    setAssociation,
    removeAssociation,
  } = useAccountUpdate(company?.id || '');

  // Backend data hooks for tabs (only fetch when sheet is open)
  const { engagements: rawMeetings, hasMore: hasMeetingMore, loadMore: loadMoreMeetings } = useAccountEngagements({
    accountId: company?.id || '',
    type: DdbEngagementType.Meeting,
    skip: !open,
  });
  const { engagements: rawEmailEngagements, hasMore: hasEmailMore, loadMore: loadMoreEmails } = useAccountEngagements({
    accountId: company?.id || '',
    type: DdbEngagementType.Email,
    skip: !open,
  });
  const { files: accountFiles, refetch: refetchFiles } = useAccountFiles(company?.id || '', { skip: !open });
  const { uploadFile, uploading, uploadProgress } = useUploadFile({
    entityType: 'account',
    entityId: company?.id || '',
    onSuccess: () => { refetchFiles(); },
  });
  const { notes: accountNotes, refetch: refetchAccountNotes } = useAccountNotes(company?.id || '', { skip: !open });
  const { deleteTask } = useTaskMutations();
  const { deleteNote } = useDeleteNote();
  // Preview mode: dummy tasks scope by company name (see dummy.ts linkedCompanies).
  const { tasks: dummyTasks } = useDummyTasks();
  // Production: tasks are notes with type=task; fetch the ones associated with
  // this account by id (this sheet previously read the dummy context + matched
  // on company name, so real tasks never appeared here). Mirrors DealDetailSheet.
  const companyTaskFilter = useMemo(() => ({ accountId: company?.id }), [company?.id]);
  const { tasks: backendCompanyTasks } = useBackendTasks({
    filter: companyTaskFilter,
    skip: !open || !company?.id,
  });
  const companyTasks = useMemo(() => {
    if (isLovablePreview()) {
      if (!company?.name) return [];
      return dummyTasks.filter(t => t.linkedCompanies?.includes(company.name));
    }
    return backendCompanyTasks.map(t => ({
      id: t.id!,
      title: t.title || 'Untitled',
      dueDate: t.dueDate || '',
      assignee: t.user?.preferredName || t.user?.givenName || '',
      completed: t.status === NoteStatusEnum.Completed,
    }));
  }, [dummyTasks, backendCompanyTasks, company?.name, company?.id]);

  // Reset overrides when company changes to avoid stale state
  useEffect(() => {
    setOverrides({});
  }, [company?.id]);

  // Fetch account details when sheet opens with a backend ID
  useEffect(() => {
    if (open && company?.id && workspaceId && !isLovablePreview()) {
      getAccount({
        variables: {
          id: company.id,
          filter: {
            workspaceId: String(workspaceId),
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAccount is a stable lazy query function; including it causes infinite re-fetch loops
  }, [open, company?.id, workspaceId]);

  // Transform backend data to Company format. The lazy query keeps the
  // previous account's response in `data` while a fetch for the newly
  // selected company is in flight, so gate on id — otherwise fast switching
  // renders the old company's fields under the new selection.
  const backendAccount = accountData?.account && accountData.account.id === company?.id ? accountData.account : undefined;
  const backendCompany = useMemo((): Company | null => {
    if (!backendAccount) {
      return null;
    }

    return {
      id: backendAccount.id,
      name: backendAccount.properties?.name || 'Unknown Company',
      website: backendAccount.domain || undefined,
      domain: backendAccount.domain || undefined,
      location: [backendAccount.properties?.city, backendAccount.properties?.state, backendAccount.properties?.country]
        .filter(Boolean).join(', ') || backendAccount.location || undefined,
      linkedin: backendAccount.properties?.linkedinUrl || undefined,
      phone: backendAccount.properties?.phone || undefined,
      industry: backendAccount.properties?.industry || undefined,
      // Employees: Match label from predefined ranges
      employees: findMatchingRange(backendAccount.properties?.employeeCountMin, backendAccount.properties?.employeeCountMax, EmployeeRanges) || formatRangeValue(backendAccount.properties?.employeeCountMin, backendAccount.properties?.employeeCountMax, ''),
      // Revenue: Match label from predefined ranges
      revenue: findMatchingRange(backendAccount.properties?.revenueMin, backendAccount.properties?.revenueMax, RevenueRanges) || formatRangeValue(backendAccount.properties?.revenueMin, backendAccount.properties?.revenueMax, '$'),
      description: backendAccount.properties?.description || backendAccount.properties?.aboutUs || undefined,
      lastEmail: backendAccount.lastEmail?.startTime
        ? formatDate(backendAccount.lastEmail.startTime)
        : undefined,
      lastMeeting: backendAccount.lastEngagement?.startTime
        ? formatDate(backendAccount.lastEngagement.startTime)
        : undefined,
      nextMeeting: backendAccount.nextEngagement?.startTime
        ? formatDate(backendAccount.nextEngagement.startTime)
        : undefined,
    };
  }, [backendAccount]);

  // Parse AI summary JSON into BlockNote blocks
  const summaryBlocks = useMemo(() => {
    if (isLovablePreview()) return null;
    if (!backendAccount?.properties?.aiSummary) return null;

    try {
      let rawContent = backendAccount.properties.aiSummary;

      if (typeof rawContent === 'string') {
        rawContent = rawContent.trim();
        const codeBlockMatch = rawContent.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
        if (codeBlockMatch) {
          rawContent = codeBlockMatch[1].trim();
        }
      }

      const parsed = typeof rawContent === 'string'
        ? JSON.parse(rawContent)
        : rawContent;

      if (Array.isArray(parsed)) {
        return summaryToBlocks(parsed as SummarySection[]);
      }

      if (parsed?.sections && Array.isArray(parsed.sections)) {
        return summaryToBlocks(parsed.sections as SummarySection[]);
      }

      if (parsed?.relationshipStatus || parsed?.recentContext || parsed?.keyTakeaways) {
        const blocks: PartialBlock[] = [];

        if (parsed.relationshipStatus) {
          blocks.push(
            { type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: "Relationship Status", styles: {} }] },
            { type: "paragraph", content: [{ type: "text" as const, text: parsed.relationshipStatus, styles: {} }] },
          );
        }
        if (parsed.recentContext) {
          blocks.push(
            { type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: "Recent Context", styles: {} }] },
            { type: "paragraph", content: [{ type: "text" as const, text: parsed.recentContext, styles: {} }] },
          );
        }
        if (parsed.keyTakeaways) {
          blocks.push(
            { type: "heading", props: { level: 3 }, content: [{ type: "text" as const, text: "Key Takeaways", styles: {} }] },
            { type: "paragraph", content: [{ type: "text" as const, text: parsed.keyTakeaways, styles: {} }] },
          );
        }

        return blocks;
      }

      return null;
    } catch (e) {
      return null;
    }
  }, [backendAccount]);

  // Transform backend engagements to list format
  const meetings = useMemo(() => {
    const transformed = rawMeetings.map((m: any) => {
      const title = m.displayName || m.description || 'Meeting';
      const attendees = m.contacts?.map((c) => {
        return getDisplayName(c.properties?.firstName, c.properties?.lastName, c.primaryEmail);
      }).filter(Boolean) || [];

      return {
        ...m,
        id: m.id,
        title,
        date: m.startTime
          ? formatDate(m.startTime)
          : '',
        duration: '30 min',
        attendees,
      };
    });

    // Sort by date descending (most recent first)
    return transformed.sort((a, b) => {
      const dateA = new Date(a.startTime || a.date || 0).getTime();
      const dateB = new Date(b.startTime || b.date || 0).getTime();
      return dateB - dateA;
    });
  }, [rawMeetings]);

  const emailEngagements = useMemo(() => {
    const transformed = rawEmailEngagements.map((e: any) => {
      const subject = e.title || e.displayName || e.description || 'Email';
      // Use metadataFrom (the actual email "From" header) for the sender,
      // matching the reference implementation (coffee-ui).
      const from = getSenderFromMetadata(e.metadataFrom);

      return {
        ...e,
        id: e.id,
        subject,
        displayName: subject,
        date: e.startTime
          ? formatDate(e.startTime)
          : '',
        from,
        senders: [from],
        direction: 'inbound' as const,
      };
    });

    // Sort by date descending (most recent first)
    return transformed.sort((a, b) => {
      const dateA = new Date(a.startTime || a.date || 0).getTime();
      const dateB = new Date(b.startTime || b.date || 0).getTime();
      return dateB - dateA;
    });
  }, [rawEmailEngagements]);

  // Sync websites from backend data (domain + additionalDomains)
  // Only sync when not manually edited to avoid overwriting user changes
  useEffect(() => {
    if (backendAccount && !websitesEdited && company) {
      const domains: string[] = [];
      if (backendAccount.domain) {
        domains.push(backendAccount.domain);
      }
      if (backendAccount.properties?.additionalDomains) {
        domains.push(...backendAccount.properties.additionalDomains.filter((d): d is string => !!d));
      }
      setWebsites(domains);
    }
  }, [backendAccount?.domain, backendAccount?.properties?.additionalDomains, websitesEdited, company]);

  // Sync associated companies from backend data. Union the legacy unlabeled
  // `associatedAccounts` (e.g. CRM-ingested links) with the new labeled
  // `accountAssociations` (relationship in `label`, account via `toAccount`), so
  // existing associations keep showing even before the backend backfills them
  // into the labeled store. Labeled entries win on id collision (see
  // mergeAssociatedCompanies).
  useEffect(() => {
    if (isLovablePreview() || !company || !backendAccount) return;
    setAssociatedCompanies(
      mergeAssociatedCompanies(backendAccount.associatedAccounts, backendAccount.accountAssociations),
    );
  }, [backendAccount?.associatedAccounts, backendAccount?.accountAssociations, company]);

  // Preview/demo mode: hydrate from the shared associations store so entries
  // added on another company's card show up here as the reverse relationship.
  useEffect(() => {
    if (!isLovablePreview() || !company) return;
    const existing = companyAssociationsStore.get(company.id) ?? [];
    setAssociatedCompanies(existing);
  }, [company?.id]);

  // Subscribe so reverse-updates written from another open card flow in live.
  useEffect(() => {
    if (!isLovablePreview() || !company) return;
    return companyAssociationsStore.subscribe(() => {
      const latest = companyAssociationsStore.get(company.id) ?? [];
      setAssociatedCompanies(latest);
    });
  }, [company?.id]);

  // "More" section: every remaining standard + custom field for the account.
  // Must be called before any early return to keep hook order stable.
  const { customFields: accountCustomFields } = useCompanyCustomFields();
  const streetAddressEnabled = useFeatureFlag(FEATURE_FLAGS.COMPANY_STREET_ADDRESS);
  const multiValuePhoneEnabled = useFeatureFlag(FEATURE_FLAGS.MULTI_VALUE_PHONE);

  // Sync visible/hidden field ordering with the Companies grid.
  // Defaults mirror CompaniesPage `defaultHiddenColumns` so a fresh user sees
  // the same hidden set in the drill-down as in the grid.
  const DEFAULT_HIDDEN_COMPANY_COLUMNS = useMemo(
    () => [
      "createdDate", "foundingYear", "city", "stateProvince", "street", "country",
      "continent", "postalCode", "companyType", "funding", "technologies", "sizeCategory",
      "lastStep", "nextStep", "source",
    ],
    [],
  );
  // Mirrors the column order in CompaniesPage (minus the sticky "name" column,
  // which is rendered as the header).
  const DEFAULT_COMPANY_COLUMN_ORDER = useMemo(
    () => [
      "website", "phone", "location", "linkedin", "industry", "employees",
      "revenue", "sizeCategory", "description", "createdBy", "createdDate",
      "foundingYear", "street", "city", "stateProvince", "country", "continent",
      "postalCode", "companyType", "funding", "technologies", "lastEmail",
      "lastMeeting", "nextMeeting", "lastStep", "nextStep", "source",
    ],
    [],
  );
  // Mirror the column layout the user actually sees on the Companies grid
  // (personal layer wins over the workspace default — see useActiveGridColumnPrefs).
  const colsState = useActiveGridColumnPrefs("companies_v3");
  const hiddenColKeys = useMemo(
    () => new Set<string>(colsState?.hiddenKeys ?? DEFAULT_HIDDEN_COMPANY_COLUMNS),
    [colsState?.hiddenKeys, DEFAULT_HIDDEN_COMPANY_COLUMNS],
  );
  const columnOrder = useMemo(
    () => (colsState?.columnOrder && colsState.columnOrder.length > 0
      ? colsState.columnOrder
      : DEFAULT_COMPANY_COLUMN_ORDER),
    [colsState?.columnOrder, DEFAULT_COMPANY_COLUMN_ORDER],
  );

  // Custom-field overrides persisted per-company for the session.
  const [moreOpen, setMoreOpen] = useState(false);
  const customOverridesKey = company?.id ? `more-fields-custom-company:${company.id}` : "";
  const [customOverrides, setCustomOverrides] = useState<Record<string, string | string[] | boolean>>({});
  useEffect(() => {
    if (!customOverridesKey) return;
    try {
      const raw = localStorage.getItem(customOverridesKey);
      setCustomOverrides(raw ? JSON.parse(raw) : {});
    } catch { setCustomOverrides({}); }
  }, [customOverridesKey]);
  const persistCustomOverrides = (next: Record<string, string | string[] | boolean>) => {
    setCustomOverrides(next);
    if (customOverridesKey) {
      try { localStorage.setItem(customOverridesKey, JSON.stringify(next)); } catch { /* ignore */ }
    }
  };

  // Descriptor for standard company columns → MorePanelField (used when a
  // standard column is hidden in the grid so it appears in "More fields").
  const standardFieldDescriptor = useCallback((key: string): MorePanelField | null => {
    const props = (backendAccount?.properties ?? {}) as Record<string, any>;
    const custom = (backendAccount?.customProperties ?? {}) as Record<string, any>;
    const eff = (k: string) => (overrides as any)[k] ?? (company as any)?.[k] ?? "";
    switch (key) {
      case "website": return { key: "website", label: t("fields.companies.website"), type: "url", value: eff("website"), icon: Globe };
      case "phone": return { key: "phone", label: t("fields.companies.phone"), type: "phone", value: eff("phone"), icon: Phone };
      case "location": return { key: "location", label: t("fields.companies.location"), type: "location", value: eff("location"), icon: MapPin };
      case "linkedin": return { key: "linkedin", label: t("fields.companies.linkedin"), type: "url", value: eff("linkedin"), icon: LinkedInIcon };
      case "industry": return { key: "industry", label: t("fields.companies.industry"), type: "text", value: eff("industry"), icon: Briefcase };
      case "employees": return { key: "employees", label: t("fields.companies.employees"), type: "single-select", value: eff("employees"), options: EmployeeRanges.filter(r => !!r.labelKey).map(r => r.storedLabel), icon: Users };
      case "revenue": return { key: "revenue", label: t("fields.companies.revenue"), type: "single-select", value: eff("revenue"), options: RevenueRanges.filter(r => !!r.labelKey).map(r => r.storedLabel), icon: DollarSign };
      case "sizeCategory": return { key: "sizeCategory", label: t("fields.companies.sizeCategory"), type: "text", value: props.companySizeCategory ?? (company as any)?.sizeCategory ?? "", readOnly: true, icon: Users };
      case "description": return { key: "description", label: t("fields.companies.description"), type: "text", value: eff("description"), icon: FileText };
      case "createdBy": return { key: "createdBy", label: t("fields.companies.createdBy"), type: "text", value: props.createdBy ?? (company as any)?.createdBy ?? "", readOnly: true, icon: UserCircle2 };
      case "createdDate": return { key: "createdDate", label: t("fields.companies.createdDate"), type: "text", value: (backendAccount as any)?.createTs ? formatDate((backendAccount as any).createTs) : (company as any)?.createdDate ?? "", readOnly: true, icon: Calendar };
      case "foundingYear": return { key: "foundingYear", label: t("fields.companies.foundingYear"), type: "number", value: props.foundedYear ?? (company as any)?.foundingYear ?? "", icon: Calendar };
      case "street": return streetAddressEnabled ? { key: "street", label: t("fields.companies.street"), type: "text", value: props.street ?? custom.street ?? (company as any)?.street ?? "", icon: MapPin } : null;
      case "city": return { key: "city", label: t("fields.companies.city"), type: "text", value: (company as any)?.city ?? "", icon: MapPin };
      case "stateProvince": return { key: "stateProvince", label: t("fields.companies.stateProvince"), type: "text", value: (company as any)?.stateProvince ?? "", icon: MapPin };
      case "country": return { key: "country", label: t("fields.companies.country"), type: "text", value: (company as any)?.country ?? "", icon: MapPin };
      case "continent": return { key: "continent", label: t("fields.companies.continent"), type: "single-select", value: props.continent ?? (company as any)?.continent ?? "", options: ["Africa", "Antarctica", "Asia", "Europe", "North America", "Oceania", "South America"], icon: MapPin };
      case "postalCode": return { key: "postalCode", label: t("fields.companies.postalCode"), type: "text", value: props.postalCode ?? (company as any)?.postalCode ?? "", icon: MapPin };
      case "companyType": return { key: "companyType", label: t("fields.companies.companyType"), type: "single-select", value: props.entityType ?? (company as any)?.companyType ?? "", options: ["Private", "Public", "Non-Profit", "Government", "Partnership"], icon: Briefcase };
      case "funding": return { key: "funding", label: t("fields.companies.funding"), type: "single-select", value: props.raisedAmount ?? (company as any)?.funding ?? "", options: ["Bootstrapped", "< $10M", "$10M-$50M", "$50M-$100M", "$100M-$250M", "$250M-$500M", "$500M-$1B", "$1B+", "IPO", "Acquired"], icon: DollarSign };
      case "technologies": return { key: "technologies", label: t("fields.companies.technologies"), type: "text", value: Array.isArray(props.techTags) ? props.techTags.join(", ") : ((company as any)?.technologies ?? ""), icon: FileText };
      case "lastEmail": return { key: "lastEmail", label: t("fields.companies.lastEmail"), type: "text", value: (company as any)?.lastEmail ?? "", readOnly: true, icon: Mail };
      case "lastMeeting": return { key: "lastMeeting", label: t("fields.companies.lastMeeting"), type: "text", value: (company as any)?.lastMeeting ?? "", readOnly: true, icon: Video };
      case "nextMeeting": return { key: "nextMeeting", label: t("fields.companies.nextMeeting"), type: "text", value: (company as any)?.nextMeeting ?? "", readOnly: true, icon: Video };
      case "lastStep": return { key: "lastStep", label: t("fields.companies.lastStep"), type: "text", value: (overrides as any).lastStep ?? props.lastActivityText ?? (company as any)?.lastStep ?? "", icon: Activity };
      case "nextStep": return { key: "nextStep", label: t("fields.companies.nextStep"), type: "text", value: (overrides as any).nextStep ?? props.nextActivityText ?? (company as any)?.nextStep ?? "", icon: Activity };
      case "source": return { key: "source", label: t("fields.companies.source"), type: "text", value: props.source ?? custom.external_link ?? (company as any)?.source ?? "", readOnly: true, icon: LinkIcon };
      default: return null;
    }
  }, [backendAccount, company, overrides, streetAddressEnabled, t, formatDate]);

  // "More fields" panel content = hidden standard columns + hidden custom columns.
  const moreFields = useMemo<MorePanelField[]>(() => {
    const custom = (backendAccount?.customProperties ?? {}) as Record<string, any>;
    const std: MorePanelField[] = [];
    for (const key of hiddenColKeys) {
      if (key === "name") continue;
      const d = standardFieldDescriptor(key);
      if (d) std.push(d);
    }
    // Include persisted grid customColumns as a fallback so custom fields
    // absent from the backend/preview registry still appear in "More fields"
    // when hidden from the grid.
    const knownKeys = new Set<string>();
    const allCustom: Array<{ key: string; label: string; type: string; selectOptions?: string[]; crmName?: string }> = [];
    for (const cf of accountCustomFields) {
      const key = `custom_${cf.crmName}`;
      knownKeys.add(key);
      allCustom.push({ key, label: cf.label, type: cf.type, selectOptions: cf.selectOptions, crmName: cf.crmName });
    }
    for (const cc of colsState?.customColumns ?? []) {
      if (!cc.key.startsWith("custom_") || knownKeys.has(cc.key)) continue;
      knownKeys.add(cc.key);
      allCustom.push({ key: cc.key, label: cc.label, type: cc.type || "text", selectOptions: cc.selectOptions });
    }
    const customRows: MorePanelField[] = allCustom
      .filter(cf => hiddenColKeys.has(cf.key) || (cf.crmName ? hiddenColKeys.has(cf.crmName) : false))
      .map(cf => {
        const key = cf.key;
        const override = customOverrides[key];
        const backendVal = cf.crmName ? custom[cf.crmName] : undefined;
        // Prefer the shared accountEditStore (grid + detail write here) so a
        // value entered in the grid appears in the detail card and vice versa.
        const storeVal = (storeOverride as any)?.[key];
        const value =
          storeVal !== undefined
            ? storeVal
            : override !== undefined
              ? override
              : (backendVal ?? "");
        return {
          key,
          label: cf.label,
          type: cf.type,
          options: cf.selectOptions,
          value: value as any,
          isCustom: true,
        };
      });
    return [...std, ...customRows];
  }, [backendAccount, accountCustomFields, hiddenColKeys, customOverrides, storeOverride, standardFieldDescriptor, colsState?.customColumns]);

  const handleMoreFieldChange = (key: string, value: string | boolean | string[]) => {
    if (key.startsWith("custom_")) {
      persistCustomOverrides({ ...customOverrides, [key]: value });
      const strVal = Array.isArray(value) ? value.join(", ") : String(value);
      if (company?.id) accountEditStore.merge(company.id, { [key]: strVal } as any);
      // Grid pages persist via onFieldUpdate → updateCell → debounced save.
      // At mounts without that wiring (Today, global search, meeting sheets)
      // save directly, or the edit would never reach the backend.
      if (!onFieldUpdate && !isLovablePreview() && company?.id) {
        scheduleCompanyFieldUpdate(company.id, key, strVal);
      }
      onFieldUpdate?.(key, strVal);
      return;
    }
    const strVal = Array.isArray(value) ? value.join(", ") : String(value);
    switch (key) {
      case "website":
      case "phone":
      case "location":
      case "linkedin":
      case "industry":
      case "description":
        updateField(key as any, strVal);
        break;
      case "employees": {
        const range = EmployeeRanges.find(r => r.storedLabel === strVal);
        if (range) {
          updateEmployees(range.min, range.max, strVal);
          setOverrides(prev => ({ ...prev, employees: strVal }));
        }
        break;
      }
      case "revenue": {
        const range = RevenueRanges.find(r => r.storedLabel === strVal);
        if (range) {
          updateRevenue(range.min, range.max, strVal);
          setOverrides(prev => ({ ...prev, revenue: strVal }));
        }
        break;
      }
      case "lastStep":
      case "nextStep":
        setOverrides(prev => ({ ...prev, [key]: strVal } as any));
        onFieldUpdate?.(key, strVal);
        break;
      default:
        setOverrides(prev => ({ ...prev, [key]: strVal } as any));
        onFieldUpdate?.(key, strVal);
        break;
    }
  };

  if (!company) return null;

  // Use backend data when available, otherwise fall back to prop data
  const useBackendData = !isLovablePreview() && backendCompany;
  const baseCompany = useBackendData ? backendCompany : company;

  // Prefer explicit local edits, then the shared account edit store, then the
  // hydrated backend/prop websites. This lets edits from the grid flow through
  // without waiting for the sheet-local state to re-sync.
  const currentWebsites = websites.length > 0
    ? websites
    : (storeOverride?.websites && storeOverride.websites.length > 0
        ? storeOverride.websites
        : (baseCompany.website ? [baseCompany.website] : []));
  const current = { ...baseCompany, ...overrides };
  const currentPhones = phones.length > 0
    ? phones
    : (current.phone ? [formatPhone(current.phone) || current.phone] : []);

  const favorited = isFavorite(company.id, "company");
  const handleToggleFavorite = () => {
    toggleFavorite({
      id: company.id,
      type: "company",
      name: current.name,
      subtitle: current.description
        ? (current.description.length > 60 ? current.description.slice(0, 60) + "…" : current.description)
        : (current.industry || ""),
      domain: current.website,
    });
  };

  const updateField = (field: keyof Company, value: string) => {
    // Optimistic update for UI
    setOverrides(prev => ({ ...prev, [field]: value }));
    // Notify parent of field change
    onFieldUpdate?.(field, value);
    // Share the edit with other surfaces (deal/contact grid rows are no-cache
    // snapshots — Pattern 49 — so they overlay this store instead of the Apollo cache)
    if (company?.id && (ACCOUNT_STORE_FIELDS as ReadonlySet<string>).has(field)) {
      accountEditStore.merge(company.id, { [field]: value } as AccountOverride);
    }

    // Update backend based on field
    switch (field) {
      case 'name':
        updateName(value);
        break;
      case 'website':
      case 'domain':
        updateDomain(value);
        break;
      case 'phone':
        updatePhone(value);
        break;
      case 'location': {
        // Parse location string back to components
        const parts = value.split(',').map(s => s.trim());
        updateLocation({
          city: parts[0] || undefined,
          state: parts[1] || undefined,
          country: parts[2] || undefined,
        });
        break;
      }
      case 'linkedin':
        updateLinkedIn(value);
        break;
      case 'industry':
        updateIndustry(value);
        break;
      // employees and revenue are read-only computed fields (not editable)
      case 'description':
        updateDescription(value);
        break;
    }
  };

  // Get people from backend or fall back to dummy data
  const backendContacts = backendAccount?.contacts || [];

  const companyPeopleRaw = useBackendData && backendContacts.length > 0
    ? backendContacts.map(contact => ({
        id: contact.id,
        name: getDisplayName(contact.properties?.firstName, contact.properties?.lastName, contact.primaryEmail),
        title: (contact.properties as any)?.jobTitle || undefined,
        company: company.name,
        email: contact.primaryEmail || undefined,
        phone: (contact.properties as any)?.phone || undefined,
      }))
    : people.filter(p => p.company === company.name);

  // Dedupe contacts so a person with multiple emails appears once.
  // Prefer id as the unique key; fall back to a normalized name when ids differ
  // but the underlying person is the same (e.g. one record per email address).
  const companyPeople = (() => {
    const byKey = new Map<string, typeof companyPeopleRaw[number]>();
    for (const p of companyPeopleRaw) {
      const nameKey = (p.name || "").trim().toLowerCase();
      const key = nameKey || p.id;
      if (!byKey.has(key)) byKey.set(key, p);
    }
    return Array.from(byKey.values());
  })();

  const handleOpenChange = (open: boolean) => {
    if (!open) setNavStack([]);
    onOpenChange(open);
  };

  const pushNav = (item: NavigationItem) => setNavStack(prev => [...prev, item]);
  const popNav = () => setNavStack(prev => prev.slice(0, -1));

  const currentNav = navStack.length > 0 ? navStack[navStack.length - 1] : null;

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent expandable hideOverlay={hideOverlay} className={cn("sm:max-w-[50vw] w-full overflow-y-auto p-0", hideOverlay && "z-[60]")}>
        {currentNav ? (
          <div className="px-6 pt-6 pb-6">
            {currentNav.type === "email" && <EmailDrillDown email={currentNav.data} onBack={popNav} />}
            {currentNav.type === "person" && (() => {
              setSelectedPerson(currentNav.data);
              setPersonSheetOpen(true);
              popNav();
              return null;
            })()}
            {currentNav.type === "meeting" && (() => {
              // Open MeetingIntelligenceSheet or MeetingBriefingSheet
              const meetingDate = new Date(currentNav.data.date || currentNav.data.startTime);
              const isUpcoming = meetingDate > new Date();
              if (isUpcoming) {
                setBriefingMeeting({
                  id: currentNav.data.id,
                  title: currentNav.data.title || currentNav.data.name || t("detailSheets.meeting"),
                  date: formatDate(meetingDate),
                  time: formatTime(meetingDate),
                  duration: currentNav.data.duration || t("detailSheets.defaultDuration"),
                  type: t("detailSheets.meeting"),
                  attendees: currentNav.data.attendees || [],
                  link: undefined,
                });
                setBriefingOpen(true);
              } else {
                setMeetingIntelligenceNote({
                  id: currentNav.data.id,
                  name: currentNav.data.title || currentNav.data.name || t("detailSheets.meeting"),
                  date: currentNav.data.date || currentNav.data.startTime,
                  people: currentNav.data.attendees || [],
                  company: company?.name || '',
                });
                setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                setMeetingIntelligenceOpen(true);
              }
              popNav();
              return null;
            })()}
            {currentNav.type === "briefing" && (() => {
              // Open MeetingBriefingSheet
              const meetingData = currentNav.data;
              setBriefingMeeting({
                id: meetingData.id,
                title: meetingData.title || meetingData.name || t("detailSheets.meeting"),
                date: meetingData.date,
                time: meetingData.time || '',
                duration: meetingData.duration || t("detailSheets.defaultDuration"),
                type: t("detailSheets.meeting"),
                attendees: meetingData.attendees || [],
                link: undefined,
              });
              setBriefingOpen(true);
              popNav();
              return null;
            })()}
            {currentNav.type === "deal" && (() => {
              // Open nested backend DealDetailSheet instead of the preview-only drill-down
              setNestedSelectedDeal(currentNav.data);
              setNestedDealSheetOpen(true);
              popNav();
              return null;
            })()}
            {currentNav.type === "company" && (() => {
              // Open nested CompanyDetailSheet instead of inline drill-down
              setNestedSelectedCompany(currentNav.data);
              setNestedCompanySheetOpen(true);
              popNav();
              return null;
            })()}
            {currentNav.type === "note" && <NoteDrillDown note={currentNav.data} onBack={popNav} />}
          </div>
        ) : accountLoading && !isLovablePreview() ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Header + Company Info */}
            <div className="px-6 pt-6 pb-4 border-b border-border">
              {onBack && (
                <button onClick={onBack} className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground mb-3 transition">
                  <ArrowLeft className="h-4 w-4" /> {t("detailSheets.back")}
                </button>
              )}
              <div className="flex items-start gap-4">
                <CompanyLogo name={company.name} domain={current.domain} size="lg" />
                <div className="flex-1 min-w-0">
                  <SheetHeader className="p-0 space-y-0">
                    <div className="flex items-center gap-2">
                      <EditableCell
                        value={current.name}
                        onChange={v => updateField("name", v)}
                        className="text-lg font-semibold w-auto min-w-0 shrink"
                      >
                        <SheetTitle className="text-lg font-semibold text-left cursor-text truncate">{current.name}</SheetTitle>
                      </EditableCell>
                      <button onClick={handleToggleFavorite} className="shrink-0 hover:scale-110 transition">
                        <Star className={cn("h-3.5 w-3.5", favorited ? "fill-accent text-accent" : "text-muted-foreground")} />
                      </button>
                      {drillDownActionsEnabled && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="shrink-0 hover:bg-accent rounded p-1 transition">
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => setAddToListOpen(true)}>
                              <ListPlus className="h-4 w-4 mr-2" /> {t("common.addToList")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConfirmDeleteOpen(true)} className="text-destructive focus:text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" /> {t("common.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </SheetHeader>
                </div>
              </div>

              {/* Editable company details — grid-synced FieldRow layout */}
              <div className="mt-4 space-y-0.5">
                {(() => {
                  const primary: Record<string, JSX.Element> = {
                    website: (
                      <FieldRow icon={Globe} label={t("fields.companies.website")}>
                        <MultiValueDetailField
                          hideIcon
                          values={currentWebsites}
                          onChange={async (urls) => {
                            setWebsitesEdited(true);
                            setWebsites(urls);
                            const domains = urls.map(url => url.replace(/^https?:\/\//, '').replace(/\/$/, ''));
                            if (company?.id) {
                              accountEditStore.merge(company.id, { websites: domains, website: domains[0] || "" });
                            }
                            await updateAdditionalDomains(
                              domains,
                              backendAccount?.domain,
                              backendAccount?.properties?.additionalDomains?.filter((d): d is string => !!d) || null
                            );
                          }}
                          icon={null}
                          isLink
                          linkPrefix="https://"
                          placeholder={t("detailSheets.addUrlPlaceholder")}
                          validate={(url) => {
                            const urlWithProtocol = /^https?:\/\/.+\..+$/;
                            const domainOnly = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?)+$/;
                            if (!urlWithProtocol.test(url) && !domainOnly.test(url)) return "Please enter a valid URL (e.g., example.com or https://example.com)";
                            return null;
                          }}
                        />
                      </FieldRow>
                    ),
                    phone: (
                      <FieldRow icon={Phone} label={t("fields.companies.phone")}>
                        {multiValuePhoneEnabled ? (
                          <MultiValueDetailField
                            hideIcon
                            values={currentPhones}
                            onChange={(next) => {
                              const normalized = next.map(v => formatPhone(v) || v);
                              setPhones(normalized);
                              updateField("phone", normalized[0] || "");
                            }}
                            icon={null}
                            placeholder={t("detailSheets.addPhonePlaceholder")}
                            validate={(phone) => {
                              const phoneRegex = /^[\d\s()+.-]{10,}$/;
                              if (phone && !phoneRegex.test(phone)) return "Please enter a valid phone number";
                              return null;
                            }}
                          />
                        ) : (
                          <EditableDetailField
                            hideIcon
                            value={formatPhone(current.phone) || current.phone || ""}
                            onChange={v => updateField("phone", formatPhone(v) || v)}
                            icon={null}
                            placeholder={t("detailSheets.setPhonePlaceholder")}
                            validate={(phone) => {
                              const phoneRegex = /^[\d\s()+.-]{10,}$/;
                              if (phone && !phoneRegex.test(phone)) return "Please enter a valid phone number";
                              return null;
                            }}
                          />
                        )}
                      </FieldRow>
                    ),
                    location: (
                      <FieldRow icon={MapPin} label={t("fields.companies.location")}>
                        <EditableLocationField compact hideIcon value={current.location || ""} onChange={v => updateField("location", v)} />
                      </FieldRow>
                    ),
                    linkedin: (
                      <FieldRow icon={LinkedInIcon} label={t("fields.companies.linkedin")}>
                        <EditableDetailField
                          hideIcon
                          value={current.linkedin || ""}
                          onChange={v => updateField("linkedin", v)}
                          icon={null}
                          isLink
                          linkPrefix="https://"
                          placeholder={t("detailSheets.setLinkedinPlaceholder")}
                          validate={(url) => validateLinkedinUrl(url, "linkedin.com/company/example")}
                        />
                      </FieldRow>
                    ),
                    industry: (
                      <FieldRow icon={Briefcase} label={t("fields.companies.industry")}>
                        <EditableIndustryField value={current.industry || ""} onChange={v => updateField("industry", v)} icon={null as any} />
                      </FieldRow>
                    ),
                    employees: (
                      <FieldRow icon={Users} label={t("fields.companies.employees")}>
                        <EditableDetailField
                          hideIcon
                          value={current.employees || ""}
                          onChange={(selectedLabel) => {
                            const selectedRange = EmployeeRanges.find(r => r.storedLabel === selectedLabel);
                            if (selectedRange) {
                              updateEmployees(selectedRange.min, selectedRange.max, selectedLabel);
                              setOverrides(prev => ({ ...prev, employees: selectedLabel }));
                            }
                          }}
                          icon={null}
                          selectOptions={EmployeeRanges.filter(r => !!r.labelKey).map(r => r.storedLabel)}
                        />
                      </FieldRow>
                    ),
                    revenue: (
                      <FieldRow icon={DollarSign} label={t("fields.companies.revenue")}>
                        <EditableDetailField
                          hideIcon
                          value={current.revenue || ""}
                          onChange={(selectedLabel) => {
                            const selectedRange = RevenueRanges.find(r => r.storedLabel === selectedLabel);
                            if (selectedRange) {
                              updateRevenue(selectedRange.min, selectedRange.max, selectedLabel);
                              setOverrides(prev => ({ ...prev, revenue: selectedLabel }));
                            }
                          }}
                          icon={null}
                          selectOptions={RevenueRanges.filter(r => !!r.labelKey).map(r => r.storedLabel)}
                        />
                      </FieldRow>
                    ),
                    lastEmail: (
                      <FieldRow icon={Clock} label={t("fields.companies.lastEmail")}>
                        {current.lastEmail && current.lastEmail !== "-" ? (
                          <button
                            onClick={() => {
                              if (useBackendData && backendAccount?.lastEmail) {
                                const emailDate = backendAccount.lastEmail.startTime
                                  ? formatDate(backendAccount.lastEmail.startTime)
                                  : '';
                                pushNav({ type: "email", data: { id: backendAccount.lastEmail.id, subject: backendAccount.lastEmail.displayName || 'Email', date: emailDate, from: company?.name || '', direction: 'inbound' as const } });
                              } else {
                                pushNav({ type: "email", data: dummyEmails[0] });
                              }
                            }}
                            className="text-xs text-foreground hover:text-accent hover:underline transition cursor-pointer text-left truncate"
                          >{current.lastEmail}</button>
                        ) : <span className="text-xs text-muted-foreground/60">—</span>}
                      </FieldRow>
                    ),
                    lastMeeting: (
                      <FieldRow icon={Calendar} label={t("fields.companies.lastMeeting")}>
                        {current.lastMeeting && current.lastMeeting !== "-" ? (
                          <button
                            onClick={() => {
                              if (useBackendData && backendAccount?.lastEngagement) {
                                setMeetingIntelligenceNote({
                                  id: backendAccount.lastEngagement.id,
                                  name: backendAccount.lastEngagement.displayName || 'Meeting',
                                  date: backendAccount.lastEngagement.startTime || '',
                                  people: [],
                                  company: current.name || '',
                                });
                                setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                                setMeetingIntelligenceOpen(true);
                              } else {
                                setMeetingIntelligenceNote({
                                  id: dummyMeetings[0]?.id || '1',
                                  name: dummyMeetings[0]?.title || 'Meeting',
                                  date: current.lastMeeting || '',
                                  people: dummyMeetings[0]?.attendees || [],
                                  company: current.name || '',
                                });
                                setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                                setMeetingIntelligenceOpen(true);
                              }
                            }}
                            className="text-xs text-foreground hover:text-accent hover:underline transition cursor-pointer text-left truncate"
                          >{current.lastMeeting}</button>
                        ) : <span className="text-xs text-muted-foreground/60">—</span>}
                      </FieldRow>
                    ),
                    nextMeeting: (
                      <FieldRow icon={Calendar} label={t("fields.companies.nextMeeting")}>
                        {current.nextMeeting && current.nextMeeting !== "-" ? (
                          <button
                            onClick={() => {
                              // Backend path: navigate to a real briefing built from the
                              // next engagement. Falls back to a dummy briefing in preview.
                              if (useBackendData && backendAccount?.nextEngagement) {
                                const meetingDate = backendAccount.nextEngagement.startTime
                                  ? formatDate(backendAccount.nextEngagement.startTime)
                                  : '';
                                const meetingTime = backendAccount.nextEngagement.startTime
                                  ? formatTime(backendAccount.nextEngagement.startTime)
                                  : '';
                                pushNav({
                                  type: "briefing",
                                  data: {
                                    id: backendAccount.nextEngagement.id,
                                    title: backendAccount.nextEngagement.displayName || 'Upcoming Meeting',
                                    date: meetingDate,
                                    time: meetingTime,
                                    duration: '60 min',
                                    attendees: [],
                                  }
                                });
                              } else {
                                setBriefingMeeting({
                                  id: 'upcoming-company',
                                  title: 'Upcoming Meeting',
                                  date: current.nextMeeting!,
                                  time: '10:00 AM',
                                  duration: '30 min',
                                  type: 'Google Meet',
                                  attendees: dummyMeetings[0]?.attendees || [],
                                  link: undefined,
                                });
                                setBriefingOpen(true);
                              }
                            }}
                            className="text-xs text-accent font-medium hover:underline transition cursor-pointer text-left truncate"
                          >{current.nextMeeting}</button>
                        ) : <span className="text-xs text-muted-foreground/60">—</span>}
                      </FieldRow>
                    ),
                    lastStep: (
                      <FieldRow icon={Activity} label={t("fields.companies.lastStep")}>
                        <EditableDetailField
                          hideIcon
                          value={String((overrides as any).lastStep ?? (backendAccount?.properties as any)?.lastActivityText ?? (current as any).lastStep ?? "")}
                          onChange={v => handleMoreFieldChange("lastStep", v)}
                          icon={null}
                          placeholder={t("detailSheets.setLastStepPlaceholder")}
                        />
                      </FieldRow>
                    ),
                    nextStep: (
                      <FieldRow icon={Activity} label={t("fields.companies.nextStep")}>
                        <EditableDetailField
                          hideIcon
                          value={String((overrides as any).nextStep ?? (backendAccount?.properties as any)?.nextActivityText ?? (current as any).nextStep ?? "")}
                          onChange={v => handleMoreFieldChange("nextStep", v)}
                          icon={null}
                          placeholder={t("detailSheets.setNextStepPlaceholder")}
                        />
                      </FieldRow>
                    ),
                    description: (
                      <FieldRow icon={FileText} label={t("fields.companies.description")}>
                        <EditableDetailField
                          hideIcon
                          value={current.description || ""}
                          onChange={v => updateField("description", v)}
                          icon={null}
                          placeholder={t("detailSheets.addDescriptionPlaceholder")}
                        />
                      </FieldRow>
                    ),
                  };

                  const customByKey = new Map<string, typeof accountCustomFields[number]>(
                    accountCustomFields.map(cf => [`custom_${cf.crmName}`, cf])
                  );
                  const customValues = (backendAccount?.customProperties ?? {}) as Record<string, any>;
                  const seen = new Set<string>();
                  const rows: JSX.Element[] = [];
                  const emit = (key: string) => {
                    if (seen.has(key) || key === "name" || hiddenColKeys.has(key)) return;
                    seen.add(key);
                    if (primary[key]) {
                      rows.push(<div key={key}>{primary[key]}</div>);
                      return;
                    }
                    // Fall back to a standard-field descriptor for simple text/date/etc rows
                    const d = standardFieldDescriptor(key);
                    if (d) {
                      rows.push(<MoreFieldRow key={key} field={d} onChange={handleMoreFieldChange} />);
                      return;
                    }
                    const cf = customByKey.get(key);
                    if (cf) {
                      const override = customOverrides[key];
                      const backendVal = customValues[cf.crmName];
                      const storeVal = (storeOverride as any)?.[key];
                      const value =
                        storeVal !== undefined
                          ? storeVal
                          : override !== undefined
                            ? override
                            : (backendVal ?? "");
                      rows.push(
                        <MoreFieldRow
                          key={key}
                          field={{ key, label: cf.label, type: cf.type, options: cf.selectOptions, value: value as any, isCustom: true }}
                          onChange={handleMoreFieldChange}
                        />
                      );
                    }
                  };
                  if (columnOrder.length > 0) {
                    // Respect the grid's visible column order/visibility strictly:
                    // hidden columns move to "More fields", not the primary list.
                    for (const k of columnOrder) emit(k);
                  } else {
                    // No persisted column state yet — show all known fields.
                    for (const k of Object.keys(primary)) emit(k);
                    for (const cf of accountCustomFields) emit(`custom_${cf.crmName}`);
                  }
                  return rows;
                })()}

                <FieldRow icon={Building2} label={t("fields.companies.associations")}>
                  <AssociatedCompanies
                    companies={associatedCompanies}
                    onChange={(next) => {
                      const prev = associatedCompanies;
                      setAssociatedCompanies(next); // optimistic

                      if (isLovablePreview() && company) {
                        companyAssociationsStore.set(
                          { id: company.id, name: company.name, domain: (company as any).website || undefined, logoUrl: (company as any).logoUrl || null },
                          next,
                        );
                        return;
                      }

                      // Production: persist the added/removed edges, then refetch the
                      // account so the canonical state (and reverse relationship) show.
                      const prevIds = new Set(prev.map((c) => c.id));
                      const nextIds = new Set(next.map((c) => c.id));
                      const added = next.filter((c) => !prevIds.has(c.id));
                      const removed = prev.filter((c) => !nextIds.has(c.id));
                      if (added.length === 0 && removed.length === 0) return;

                      void (async () => {
                        await Promise.all([
                          ...added.map((c) => setAssociation(c.id, c.relationship || 'partner')),
                          ...removed.map((c) => removeAssociation(c.id, c.relationship || 'partner')),
                        ]);
                        if (company?.id && workspaceId) {
                          getAccount({
                            variables: {
                              id: company.id,
                              filter: { workspaceId: String(workspaceId) },
                            },
                          });
                        }
                      })();
                    }}
                    excludeId={company.id}
                    onCompanyClick={(c) => {
                      setNestedSelectedCompany({ id: c.id, name: c.name, website: c.domain || undefined });
                      setNestedCompanySheetOpen(true);
                    }}
                  />
                </FieldRow>

                <FieldRow icon={ListIcon} label={t("detailSheets.lists")}>
                  <EntityListBadges type="company" id={current.id} compact />
                </FieldRow>

                {/* Deals */}
                {(() => {
                  const companyDeals: DealItem[] = useBackendData
                    ? (backendAccount?.deals?.edges ?? []).map(({ node }) => ({
                        id: node.id,
                        name: node.properties?.name || t("detailSheets.untitledDeal"),
                        stage: node.properties?.stage || undefined,
                        amount: node.properties?.amount ?? undefined,
                        company: current.name,
                      }))
                    : allDeals.filter(d => d.company === current.name);
                  if (companyDeals.length === 0) return null;
                  return (
                    <FieldRow icon={Briefcase} label={t("deals.title")}>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs min-w-0">
                        {companyDeals.map((d, i) => (
                          <span key={d.id} className="min-w-0">
                            <button
                              onClick={() => pushNav({ type: "deal", data: d })}
                              className="text-accent font-medium hover:underline transition cursor-pointer truncate"
                            >{d.name}</button>
                            {i < companyDeals.length - 1 && <span className="text-muted-foreground">,</span>}
                          </span>
                        ))}
                      </div>
                    </FieldRow>
                  );
                })()}

                <button
                  type="button"
                  onClick={() => setMoreOpen(v => !v)}
                  aria-expanded={moreOpen}
                  className="mt-1 inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition"
                >
                  {moreOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                  <span>{moreOpen ? t("detailSheets.fewerFields") : t("detailSheets.moreFields")}</span>
                </button>
                <MoreFieldsPanel
                  open={moreOpen}
                  onClose={() => setMoreOpen(false)}
                  entity="companies"
                  fields={moreFields}
                  onChange={handleMoreFieldChange}
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 pb-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className={cn("w-full grid mb-5", callsTabEnabled ? "grid-cols-8" : "grid-cols-7")}>
                  <TabsTrigger value="summary" className="text-[11px] gap-1"><Sparkles className="h-3 w-3" />{t("detailSheets.tabSummary")}</TabsTrigger>
                  <TabsTrigger value="notes" className="text-[11px] gap-1"><StickyNote className="h-3 w-3" />{t("common.notes")}</TabsTrigger>
                  <TabsTrigger value="tasks" className="text-[11px] gap-1"><CheckSquare className="h-3 w-3" />{t("tasks.title")}</TabsTrigger>
                  <TabsTrigger value="people" className="text-[11px] gap-1"><Users className="h-3 w-3" />{t("people.title")}</TabsTrigger>
                  <TabsTrigger value="meetings" className="text-[11px] gap-1"><Video className="h-3 w-3" />{t("detailSheets.tabMeetings")}</TabsTrigger>
                  <TabsTrigger value="emails" className="text-[11px] gap-1"><Mail className="h-3 w-3" />{t("detailSheets.tabEmails")}</TabsTrigger>
                  {callsTabEnabled && <TabsTrigger value="calls" className="text-[11px] gap-1"><Phone className="h-3 w-3" />{t("detailSheets.tabCalls")}</TabsTrigger>}
                  <TabsTrigger value="files" className="text-[11px] gap-1"><Paperclip className="h-3 w-3" />{t("detailSheets.tabFiles")}</TabsTrigger>
                </TabsList>

                {/* Summary */}
                <TabsContent value="summary" className="space-y-4">
                  {summaryBlocks ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground px-4">
                        <Sparkles className="h-3 w-3 text-accent" />
                        <span>{t("detailSheets.aiGeneratedSummary")}</span>
                      </div>
                      <NoteEditor
                        initialContent={summaryBlocks}
                        onChange={() => {}}
                        autoFocus={false}
                        editable={false}
                      />
                      {backendAccount?.asOfTs && (
                        <p className="text-[10px] text-muted-foreground pt-1 px-4">
                          {t("detailSheets.lastUpdatedAt", { timestamp: formatDateTime(backendAccount.asOfTs) })}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Sparkles className="h-3 w-3 text-accent" />
                        <span>{t("detailSheets.aiGeneratedSummary")}</span>
                      </div>
                      {isLovablePreview() ? (
                        <div className="space-y-2 text-sm leading-relaxed">
                          <p>
                            <LinkedText
                              text={`${company.name} is an active account with strong engagement over the past quarter.${companyPeople.length > 0 ? ` You're primarily working with ${companyPeople.slice(0, 2).map(p => p.name).join(" and ")}.` : ""}`}
                              onPersonClick={(p) => pushNav({ type: "person", data: { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone } })}
                              onCompanyClick={(c) => pushNav({ type: "company", data: { id: c.id, name: c.name, industry: c.industry, description: c.description, location: c.location, website: c.website, employees: c.employees, revenue: c.revenue } })}
                            />
                          </p>
                          <p>
                            <LinkedText
                              // eslint-disable-next-line coffee-i18n/no-hardcoded-plural -- Lovable-preview dummy narrative; never rendered in production.
                              text={`Communication has been consistent with ${dummyEmails.length} recent email threads and ${dummyMeetings.length} meetings logged. The most recent interaction was an email on ${dummyEmails[0]?.date} regarding "${dummyEmails[0]?.subject}."`}
                              onPersonClick={(p) => pushNav({ type: "person", data: { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone } })}
                              onCompanyClick={(c) => pushNav({ type: "company", data: { id: c.id, name: c.name, industry: c.industry, description: c.description, location: c.location, website: c.website, employees: c.employees, revenue: c.revenue } })}
                            />
                          </p>
                          <p>
                            <Trans
                              i18nKey="detailSheets.companyRelationshipHealth"
                              components={{ strong: <span className="text-emerald-500 font-medium" /> }}
                            />
                          </p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-xs italic">
                          {t("detailSheets.noAiSummary")}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground pt-1">{t("detailSheets.summaryLastUpdated")}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Notes */}
                <TabsContent value="notes">
                  <NotesTable
                    notes={accountNotes.map(n => ({ id: n.id, name: n.title, date: n.date, type: n.type, content: n.content }))}
                    onRowClick={n => pushNav({ type: "note", data: n as NoteItem })}
                    onAdd={() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: { type: "note", association: company ? { id: company.id, type: "company", name: company.name } : undefined } }))}
                    onDelete={n => deleteNote(n.id, refetchAccountNotes)}
                  />
                </TabsContent>

                {/* Tasks */}
                <TabsContent value="tasks">
                  <TasksTable
                    tasks={companyTasks.map(t => ({ id: t.id, title: t.title, dueDate: t.dueDate, assignee: t.assignee, completed: t.completed }))}
                    onRowClick={t => setSelectedTaskId(t.id)}
                    onAdd={() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: { type: "task", association: company ? { id: company.id, type: "company", name: company.name } : undefined } }))}
                    onDelete={t => { if (!isLovablePreview()) deleteTask(t.id); }}
                  />
                </TabsContent>

                {/* People */}
                <TabsContent value="people">
                  <PeopleTable
                    people={companyPeople.map(p => ({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone }))}
                    onRowClick={p => pushNav({ type: "person", data: p })}
                    emptyMessage={t("detailSheets.noPeopleAtCompany")}
                  />
                </TabsContent>

                {/* Meetings */}
                <TabsContent value="meetings">
                  <MeetingsTable
                    meetings={(isLovablePreview() ? dummyMeetings : meetings)}
                    onRowClick={(m) => {
                      const meetingDate = new Date((m as any).startTime || m.date);
                      const isUpcoming = meetingDate > new Date();

                      if (isUpcoming) {
                        setBriefingMeeting({
                          id: m.id,
                          title: (m as any).displayName || (m as any).name || m.title || 'Meeting',
                          date: formatDate(meetingDate),
                          time: formatTime(meetingDate),
                          duration: '30 min',
                          type: 'Meeting',
                          attendees: (m as any).contacts?.map((c: any) => [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail).filter(Boolean) || m.attendees || [],
                          link: undefined,
                        });
                        setBriefingOpen(true);
                      } else {
                        setMeetingIntelligenceNote({
                          id: m.id,
                          name: (m as any).displayName || (m as any).name || m.title || 'Meeting',
                          date: (m as any).startTime || m.date,
                          people: (m as any).contacts?.map((c: any) => [c.properties?.firstName, c.properties?.lastName].filter(Boolean).join(' ') || c.primaryEmail).filter(Boolean) || m.attendees || [],
                          company: company?.name || '',
                        });
                        setMeetingIntelligenceTab(SUMMARY_TAB_ID);
                        setMeetingIntelligenceOpen(true);
                      }
                    }}
                    onAttendeeClick={async (name) => {
                      const allMeetings = isLovablePreview() ? dummyMeetings : meetings;
                      for (const m of allMeetings) {
                        const contact = (m as any).contacts?.find((c: any) => {
                          const fullName = `${c?.properties?.firstName || ''} ${c?.properties?.lastName || ''}`.trim();
                          return fullName === name || c?.primaryEmail === name;
                        });
                        if (contact) {
                          setSelectedPerson({
                            id: contact.id,
                            name: `${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail || name,
                            title: contact.properties?.jobTitle || '',
                            company: contact.accounts?.edges?.[0]?.node?.properties?.name || (contact as any).accounts?.[0]?.properties?.name || '',
                            email: contact.primaryEmail || '',
                            phone: contact.properties?.phone || '',
                          });
                          setPersonSheetOpen(true);
                          return;
                        }
                      }
                      // Attendee isn't among the loaded meetings' contacts:
                      // resolve the name to a real record (backend search in
                      // production, dummy people in preview).
                      const p = await resolvePerson(name);
                      if (p) {
                        setSelectedPerson({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone });
                        setPersonSheetOpen(true);
                      }
                    }}
                  />
                  {!isLovablePreview() && hasMeetingMore && (
                    <div className="flex justify-center py-4">
                      <Button onClick={loadMoreMeetings} variant="outline" size="sm">
                        {t("detailSheets.loadMore")}
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Emails */}
                <TabsContent value="emails">
                  <EmailsTable
                    emails={(isLovablePreview() ? dummyEmails : emailEngagements)}
                    onRowClick={(e) => {
                      pushNav({
                        type: "email",
                        data: {
                          id: e.id,
                          subject: e.subject || (e as any).displayName || t("detailSheets.emailFallbackSubject"),
                          date: e.date || (e as any).startTime,
                          from: e.from || t("detailSheets.unknownName"),
                          direction: e.direction || 'inbound',
                        },
                      });
                    }}
                    onSenderClick={async (name) => {
                      const p = await resolvePerson(name);
                      if (p) {
                        setSelectedPerson({ id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone });
                        setPersonSheetOpen(true);
                      }
                    }}
                  />
                  {!isLovablePreview() && hasEmailMore && (
                    <div className="flex justify-center py-4">
                      <Button onClick={loadMoreEmails} variant="outline" size="sm">
                        {t("detailSheets.loadMore")}
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Calls */}
                {callsTabEnabled && (
                  <TabsContent value="calls">
                    <RecordCallsTab recordType="account" recordId={company?.id || ''} enabled={open && callsTabEnabled} />
                  </TabsContent>
                )}

                {/* Files */}
                <TabsContent value="files">
                  <FilesTable
                    files={isLovablePreview() ? dummyFiles : accountFiles}
                    onRowClick={f => pushNav({ type: "file", data: f })}
                    onFileUpload={uploadFile}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>

    {/* Meeting Intelligence Sheet - stacked on top */}
    <MeetingIntelligenceSheet
      open={meetingIntelligenceOpen}
      onOpenChange={setMeetingIntelligenceOpen}
      note={meetingIntelligenceNote}
      defaultTab={meetingIntelligenceTab}
      hideOverlay={true}
      onBack={() => setMeetingIntelligenceOpen(false)}
    />

    {/* Meeting Briefing Sheet - stacked on top */}
    <MeetingBriefingSheet
      open={briefingOpen}
      onOpenChange={setBriefingOpen}
      meeting={briefingMeeting}
      isRecording={false}
      onRecordingChange={() => {}}
      hideOverlay={true}
      onBack={() => setBriefingOpen(false)}
    />

    {/* Nested PersonDetailSheet */}
    <PersonDetailSheet
      open={personSheetOpen}
      onOpenChange={setPersonSheetOpen}
      person={selectedPerson}
      hideOverlay={true}
      onBack={() => setPersonSheetOpen(false)}
    />

    {/* TaskDetailSheet - stacked on top */}
    <TaskDetailSheet
      taskId={selectedTaskId}
      open={!!selectedTaskId}
      onOpenChange={(o) => { if (!o) setSelectedTaskId(null); }}
      hideOverlay={true}
      onBack={() => setSelectedTaskId(null)}
    />

    {/* Nested CompanyDetailSheet - for clicking on related companies */}
    <CompanyDetailSheet
      open={nestedCompanySheetOpen}
      onOpenChange={setNestedCompanySheetOpen}
      company={nestedSelectedCompany}
      onBack={() => setNestedCompanySheetOpen(false)}
    />

    {/* Nested DealDetailSheet - for clicking on this company's deals */}
    {nestedSelectedDeal && (
      <Suspense fallback={null}>
        <DealDetailSheet
          open={nestedDealSheetOpen}
          onOpenChange={setNestedDealSheetOpen}
          deal={nestedSelectedDeal}
          hideOverlay={true}
          onBack={() => setNestedDealSheetOpen(false)}
        />
      </Suspense>
    )}

    {/* `entityLabel` is an identifier, not a label: AddToListDialog keys its
        model-type, route, folder-category and refetch-query maps off it. */}
    <AddToListDialog
      open={addToListOpen}
      onOpenChange={setAddToListOpen}
      count={1}
      entityLabel="company"
      recordIds={company ? [company.id] : []}
      onConfirm={(listName) => toast.success(t("detailSheets.addedToList", { name: current.name, list: listName }))}
    />
    <ConfirmDeleteDialog
      open={confirmDeleteOpen}
      onOpenChange={setConfirmDeleteOpen}
      title={t("detailSheets.deleteRecordTitle", { name: current.name })}
      description={t("detailSheets.deleteCompanyDescription")}
      onConfirm={async () => {
        // Parent owns the delete on its home page (Companies); everywhere else
        // (Deals, Notes, Today, global nav, stacked sheets) the sheet deletes
        // itself so the button isn't a silent no-op. deleteAccount handles
        // RBAC, toast, and cache.
        const action = resolveSheetDeleteAction({
          id: company?.id,
          hasParentHandler: !!onDelete,
          isPreview: isLovablePreview(),
        });
        if (action === 'parent') onDelete!(company!.id);
        else if (action === 'self') await deleteAccount(company!.id);
        onOpenChange(false);
      }}
    />
    </>
  );
}

function FieldRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 items-center py-0.5 min-h-6 [&_.text-sm]:text-xs">
      <div className="flex items-center gap-2 text-xs leading-5 text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="relative min-w-0 flex items-center text-xs leading-5">{children}</div>
    </div>
  );
}
